import React from 'react';
import { Box, Container, Grid, Typography, IconButton, Divider, Link as MuiLink } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Link } from 'react-router-dom';

const footerLinks = {
  Institución: [
    { label: 'Nosotros', path: '/nosotros' },
    { label: 'Misión y Visión', path: '/mision-vision' },
    { label: 'Principios', path: '/principios' },
    { label: 'Valores', path: '/valores' },
    { label: 'Nuestra Red', path: '/nuestra-red' },
  ],
  Servicios: [
    { label: 'Escuelas Agroindustriales', path: '/escuelas-agroindustriales' },
    { label: 'Redes Empresariales', path: '/redes-empresariales' },
    { label: 'Banco de Proyectos', path: '/banco-proyectos' },
    { label: 'Mercado Asegurado', path: '/mercado-asegurado' },
    { label: 'Banco de Innovación', path: '/banco-innovacion' },
  ],
  Legal: [
    { label: 'Políticas de Calidad', path: '/politicas-calidad' },
    { label: 'Políticas de Privacidad', path: '/politicas-privacidad' },
    { label: 'Contacto', path: '/contacto' },
    { label: 'Afiliación', path: '/afiliacion' },
  ],
};

export default function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(180deg, #1b5e20 0%, #0d3b12 100%)',
        color: 'rgba(255,255,255,0.80)',
        pt: 8, pb: 4,
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
          background: 'linear-gradient(90deg, transparent, #a5d6a7, #43a047, #a5d6a7, transparent)',
        },
      }}
    >
      {/* Organic background shapes */}
      <Box sx={{
        position: 'absolute', top: -80, right: -80,
        width: 300, height: 300,
        borderRadius: '60% 40% 70% 30% / 40% 60% 30% 70%',
        background: 'rgba(67,160,71,0.08)', pointerEvents: 'none',
      }} />

      <Container maxWidth="xl">
        <Grid container spacing={6}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{
                width: 46, height: 46, borderRadius: '50%',
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AgricultureIcon sx={{ color: '#ffffff', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: '1.5rem', color: '#ffffff', lineHeight: 1 }}>
                  FENAGRO
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: '#a5d6a7', textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif" }}>
                  APL · Red Agroindustrial
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.62)', lineHeight: 1.82, mb: 3, maxWidth: 320 }}>
              Red Empresarial Socio-Económica Colaborativa de segundo nivel, comprometida con el desarrollo sostenible del campo colombiano.
            </Typography>

            {[
              { icon: <LocationOnIcon sx={{ fontSize: 15 }} />, text: 'Medellín, Antioquia — Edificio Santa Elena Centro' },
              { icon: <EmailIcon sx={{ fontSize: 15 }} />, text: 'fondoemprendimientofenagro@gmail.com' },
              { icon: <PhoneIcon sx={{ fontSize: 15 }} />, text: '300 776 5400' },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1.4 }}>
                <Box sx={{ color: '#a5d6a7', mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>{item.text}</Typography>
              </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              {[FacebookIcon, InstagramIcon, YouTubeIcon, WhatsAppIcon].map((Icon, i) => (
                <IconButton
                  key={i}
                  size="small"
                  sx={{
                    border: '1px solid rgba(165,214,167,0.25)',
                    color: 'rgba(255,255,255,0.55)',
                    width: 36, height: 36,
                    '&:hover': { background: 'rgba(165,214,167,0.15)', color: '#a5d6a7', borderColor: '#a5d6a7' },
                    transition: 'all 0.25s ease',
                  }}
                >
                  <Icon sx={{ fontSize: 17 }} />
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <Grid item xs={12} sm={4} md={2.5} key={title}>
              <Typography sx={{
                fontSize: '0.68rem', letterSpacing: '0.18em', color: '#a5d6a7',
                textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif",
                fontWeight: 700, mb: 2.5,
              }}>
                {title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {links.map((link) => (
                  <MuiLink
                    key={link.path}
                    component={Link}
                    to={link.path}
                    underline="none"
                    sx={{
                      fontSize: '0.84rem', color: 'rgba(255,255,255,0.60)',
                      transition: 'all 0.2s ease',
                      display: 'flex', alignItems: 'center', gap: 0.8,
                      '&:hover': { color: '#a5d6a7', paddingLeft: '5px' },
                      '&::before': { content: '"›"', opacity: 0.45 },
                    }}
                  >
                    {link.label}
                  </MuiLink>
                ))}
              </Box>
            </Grid>
          ))}

          {/* Allies */}
          <Grid item xs={12} md={3}>
            <Typography sx={{
              fontSize: '0.68rem', letterSpacing: '0.18em', color: '#a5d6a7',
              textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif",
              fontWeight: 700, mb: 2.5,
            }}>
              Alianzas
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {['Holdin Socioempresarial Solidario', 'ECOSO', 'MERCOSUR'].map((a) => (
                <Box
                  key={a}
                  sx={{
                    px: 2, py: 1.2,
                    border: '1px solid rgba(165,214,167,0.18)',
                    borderRadius: 2,
                    fontSize: '0.8rem', color: 'rgba(255,255,255,0.60)',
                    fontFamily: "'Outfit', sans-serif", cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    '&:hover': { borderColor: 'rgba(165,214,167,0.45)', background: 'rgba(165,214,167,0.06)', color: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  {a}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.07)' }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.30)' }}>
            © {new Date().getFullYear()} FENAGRO APL · Powered by Redeprog
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {[
              { label: 'Políticas de Privacidad', path: '/politicas-privacidad' },
              { label: 'Términos y Condiciones', path: '/terminos-condiciones' },
            ].map(({ label, path }) => (
              <Typography
                key={label}
                component="a"
                href={path}
                sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.28)', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: '#a5d6a7' }, transition: 'color 0.2s ease' }}
              >
                {label}
              </Typography>
            ))}
            <Typography
              component="a" href="/login"
              sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.14)', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'rgba(255,255,255,0.35)' }, transition: 'color 0.2s ease' }}
            >
              Admin
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
